import { getResultColumns, ResultColumn, resultGridMinWidth, resultGridTemplate } from './result-columns';
import { useAuthentication } from '~/hooks/useAuthentication';
import { CSSProperties, ReactNode } from 'react';
import { Registration } from '~/apis/ssf';
import { ResultRow } from './result-row';
import styles from './result-grid.module.css';
import classNames from 'classnames';

/** The grid chrome — horizontal scroll, min-width and the shared column header. Wraps any rows. */
export function ResultGridFrame({
  columns,
  template,
  minWidth,
  showHeader = true,
  children,
}: {
  columns: ResultColumn[];
  template: string;
  minWidth: number;
  showHeader?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={styles.scroll}>
      <div className={styles.grid} style={{ minWidth }}>
        {showHeader && (
          <div className={styles.header} style={{ '--cols': template } as CSSProperties}>
            {columns.map(col => (
              <div
                key={col.key}
                className={classNames(styles.headerCell, col.align === 'center' && styles.headerCenter)}
                title={col.label || undefined}
              >
                {col.short ?? col.label}
              </div>
            ))}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

interface Props {
  lifters?: Registration[];
  competitionId: number;
  isTeamComp: boolean;
  isLoading?: boolean;
}

export function ResultGrid({ lifters, competitionId, isTeamComp, isLoading }: Props) {
  const { isSuperUser } = useAuthentication();
  const columns = getResultColumns({ isTeamComp, isSuperUser });
  const template = resultGridTemplate(columns);
  const minWidth = resultGridMinWidth(columns);

  if (isLoading) {
    return <p className={styles.empty}>Laddar lyftare…</p>;
  }

  if (!lifters || lifters.length === 0) {
    return <p className={styles.empty}>Inga registrerade lyftare väntar på resultat.</p>;
  }

  return (
    <ResultGridFrame columns={columns} template={template} minWidth={minWidth}>
      {lifters.map(reg => (
        <ResultRow
          key={reg.id}
          lifterId={reg.person.id}
          name={`${reg.person.first_name} ${reg.person.last_name}`}
          meta={`${reg.person.license_id} · ${reg.person.club.name}`}
          teamId={reg.team?.id}
          competitionId={competitionId}
          isTeamComp={isTeamComp}
          columns={columns}
          template={template}
        />
      ))}
    </ResultGridFrame>
  );
}
